import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../api/users';
import { fetchHubupCommunities } from '../../api/hubup';
import { fetchCompanies } from '../../api/companies';

const permissionOptions = [
  { value: 'team', label: 'Équipe' },
  { value: 'admin', label: 'Administrateur' },
  { value: 'utilisateur', label: 'Utilisateur' },
];

const AccountsManage = () => {
  const location = useLocation();
  const inHubupShell = /\/hubup\/g\/[^/]+\/accounts-manage/.test(
    location.pathname,
  );

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    permission: 'team',
    hubup: false,
    hubupCatchall: false,
    crm: false,
    hubupCommunityIds: [],
    companyIds: [],
    affiliateCode: '',
  });
  const [hubupCommunities, setHubupCommunities] = useState([]);
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [hubupCommunitiesLoading, setHubupCommunitiesLoading] = useState(false);
  const [hubupCommunitiesError, setHubupCommunitiesError] = useState(null);

  const needsHubupCommunityPick =
    formData.hubup === true && formData.permission !== 'admin';

  const needsCompanyPick =
    formData.crm === true && formData.permission !== 'admin';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchUsers();
        setUsers(res?.data?.users || []);
      } catch (e) {
        console.error(e);
        setError('Impossible de charger les comptes.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!showForm || !needsHubupCommunityPick) {
      return;
    }
    let cancelled = false;
    setHubupCommunitiesLoading(true);
    setHubupCommunitiesError(null);
    fetchHubupCommunities()
      .then((res) => {
        if (cancelled) return;
        setHubupCommunities(res?.data?.communities || []);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setHubupCommunitiesError(
            'Impossible de charger la liste des communautés HubUP.',
          );
          setHubupCommunities([]);
        }
      })
      .finally(() => {
        if (!cancelled) setHubupCommunitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showForm, needsHubupCommunityPick]);

  useEffect(() => {
    if (!showForm || !needsCompanyPick) return;
    let cancelled = false;
    fetchCompanies()
      .then((res) => {
        if (!cancelled) setCrmCompanies(res?.data?.companies || []);
      })
      .catch(() => {
        if (!cancelled) setCrmCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showForm, needsCompanyPick]);

  const openCreate = () => {
    setEditingUser(null);
    setHubupCommunitiesError(null);
    setFormData({
      email: '',
      password: '',
      permission: 'team',
      hubup: false,
      hubupCatchall: false,
      crm: false,
      hubupCommunityIds: [],
      companyIds: [],
      affiliateCode: '',
    });
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setHubupCommunitiesError(null);
    setFormData({
      email: user.email,
      password: '',
      permission: user.permission || 'team',
      hubup: user.hubup === true,
      hubupCatchall: user.hubupCatchall === true,
      crm: user.crm === true,
      hubupCommunityIds: Array.isArray(user.hubupCommunityIds)
        ? [...user.hubupCommunityIds]
        : [],
      companyIds: Array.isArray(user.companyIds) ? [...user.companyIds] : [],
      affiliateCode: user.affiliateCode ? String(user.affiliateCode) : '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: formData.email,
        permission: formData.permission,
        hubup: formData.hubup,
        hubupCatchall: formData.hubup ? formData.hubupCatchall === true : false,
        crm: formData.crm,
        hubupCommunityIds:
          formData.hubup === true && formData.permission !== 'admin'
            ? formData.hubupCommunityIds
            : [],
        companyIds:
          formData.crm === true && formData.permission !== 'admin'
            ? formData.companyIds
            : [],
        affiliateCode: formData.affiliateCode?.trim()
          ? formData.affiliateCode.trim()
          : null,
      };
      if (!editingUser || formData.password) {
        payload.password = formData.password;
      }

      if (editingUser) {
        const res = await updateUser(editingUser._id, payload);
        const updated = res?.data?.user;
        if (updated) {
          setUsers((prev) => prev.map((u) => (u._id === updated._id ? updated : u)));
        }
      } else {
        const res = await createUser(payload);
        const created = res?.data?.user;
        if (created) {
          setUsers((prev) => [created, ...prev]);
        }
      }
      setShowForm(false);
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la sauvegarde de l'utilisateur.");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur ${user.email} ?`)) return;
    try {
      await deleteUser(user._id);
      setUsers((prev) => prev.filter((u) => u._id !== user._id));
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la suppression de l'utilisateur.");
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPermissionLabel = (value) =>
    permissionOptions.find((p) => p.value === value)?.label || value;

  return (
    <div className={inHubupShell ? '' : 'min-h-screen bg-background'}>
      <Helmet>
        <title>Gestion des comptes - CRM</title>
      </Helmet>
      {!inHubupShell ? <Header /> : null}
      {!inHubupShell ? <Breadcrumb /> : null}
      {!inHubupShell ? <FilterPanel /> : null}

      <main className={inHubupShell ? 'main-content px-4 md:px-6 lg:px-8 pb-8' : 'main-content'}>
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Gestion des comptes
              </h1>
            </div>
            <Button
              onClick={openCreate}
              iconName="UserPlus"
              iconPosition="left"
              size="lg"
            >
              Ajouter un membre
            </Button>
          </div>

          {error && (
            <p className="text-xs text-error mb-2">
              {error}
            </p>
          )}
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Comptes utilisateurs
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    HubUP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    CRM
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Comm. HubUP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Code affilié
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Dernière connexion
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Créé le
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={9}>
                      Chargement de l&apos;équipe...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={9}>
                      Aucun utilisateur pour le moment.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user._id} className="hover:bg-muted/50 transition-smooth">
                      <td className="px-4 py-3 text-sm text-foreground">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {getPermissionLabel(user.permission)}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {user.hubup === true ? 'Oui' : 'Non'}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {user.crm === true ? 'Oui' : 'Non'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {user.permission === 'admin' || user.hubup !== true
                          ? '—'
                          : Array.isArray(user.hubupCommunityIds)
                            ? user.hubupCommunityIds.length
                            : 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono text-xs">
                        {user.affiliateCode ? String(user.affiliateCode) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(user.lastLogin)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Modifier"
                            onClick={() => openEdit(user)}
                          >
                            <Icon name="Pencil" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Supprimer"
                            onClick={() => handleDelete(user)}
                          >
                            <Icon name="Trash2" size={16} className="text-error" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className={`bg-card rounded-xl shadow-elevation-3 w-full max-h-[90vh] overflow-y-auto ${
              needsHubupCommunityPick ? 'max-w-lg' : 'max-w-md'
            }`}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">
                {editingUser ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={() => setShowForm(false)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Mot de passe {editingUser && <span className="text-muted-foreground">(laisser vide pour ne pas changer)</span>}
                </label>
                <input
                  type="password"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, password: e.target.value }))
                  }
                  placeholder={editingUser ? 'Laisser vide pour conserver le mot de passe' : ''}
                  {...(!editingUser ? { required: true } : {})}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Rôle
                </label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.permission}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      permission: v,
                      hubupCommunityIds: v === 'admin' ? [] : prev.hubupCommunityIds,
                    }));
                  }}
                >
                  {permissionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Code affilié HubUP
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.affiliateCode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      affiliateCode: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="Code promo"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-3 pt-1">
                <Checkbox
                  checked={formData.hubup}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      hubup: on,
                      hubupCommunityIds: on ? prev.hubupCommunityIds : [],
                      hubupCatchall: on ? prev.hubupCatchall : false,
                    }));
                  }}
                  label="Accès Hubup"
                />
                <Checkbox
                  checked={formData.hubupCatchall}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hubupCatchall: e.target.checked,
                    }))
                  }
                  disabled={!formData.hubup}
                  label="Notification par email"
                />
                <Checkbox
                  checked={formData.crm}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      crm: e.target.checked,
                      companyIds: e.target.checked ? prev.companyIds : [],
                    }))
                  }
                  label="Accès CRM"
                />
              </div>

              {needsCompanyPick && (
                <div className="space-y-2 border-t border-border pt-3">
                  <p className="text-xs font-medium text-foreground">
                    Entreprises CRM assignées
                  </p>
                  <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2 space-y-2 bg-muted/20">
                    {crmCompanies.map((c) => {
                      const id = String(c._id);
                      const checked = formData.companyIds.includes(id);
                      return (
                        <Checkbox
                          key={id}
                          checked={checked}
                          onChange={() =>
                            setFormData((prev) => {
                              const set = new Set(prev.companyIds.map(String));
                              if (set.has(id)) set.delete(id);
                              else set.add(id);
                              return { ...prev, companyIds: [...set] };
                            })
                          }
                          label={c.name}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {needsHubupCommunityPick && (
                <div className="space-y-2 border-t border-border pt-3">
                  <p className="text-xs font-medium text-foreground">
                    Communautés HubUP assignées
                  </p>
                  {hubupCommunitiesLoading && (
                    <p className="text-xs text-muted-foreground">Chargement des communautés…</p>
                  )}
                  {hubupCommunitiesError && (
                    <p className="text-xs text-error">{hubupCommunitiesError}</p>
                  )}
                  {!hubupCommunitiesLoading &&
                    !hubupCommunitiesError &&
                    hubupCommunities.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Aucune communauté disponible.
                      </p>
                    )}
                  <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2 space-y-2 bg-muted/20">
                    {hubupCommunities.map((c) => {
                      const checked = formData.hubupCommunityIds.includes(c.id);
                      return (
                        <Checkbox
                          key={c.id}
                          checked={checked}
                          onChange={() =>
                            setFormData((prev) => {
                              const set = new Set(prev.hubupCommunityIds);
                              if (set.has(c.id)) set.delete(c.id);
                              else set.add(c.id);
                              return {
                                ...prev,
                                hubupCommunityIds: [...set],
                              };
                            })
                          }
                          label={c.name}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="success" iconName="Save" iconPosition="left">
                  Sauvegarder
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsManage;

